import { DictType } from "@pma/types";
import { useEffect, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/common/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/common/popover";
import { cn } from "@pma/utils/helpers";
import { Button } from "@components/common/button";
import axios from "axios";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { useAuth } from "@stores/auth-store";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  DEPARTMENTS_DICTIONARY_ENDPOINT,
  EDIT_USER_ORG_PARAMETER_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes";
import useTranslator from "@hooks/use-translator";
import { Separator } from "@components/common/separator";
import { toast } from "@hooks/use-toast";
import AddDepartment from "./add-department";

interface Props {
  department?: string;
  refetch: CallableFunction;
  userId: string;
}
const DepartmentsSelector = ({ department, userId, refetch }: Props) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(department);

  const { authConfig } = useAuth();

  const { translate, locale } = useTranslator();

  const isHebrew = locale === "he";

  const accessToken = parseCookies()[ACCESS_TOKEN];
  const orgId = authConfig?.last_organization_id;

  const getDepartments = async () => {
    const response = await axios.get(
      SERVER_URL +
        `${DEPARTMENTS_DICTIONARY_ENDPOINT}?context_type=organization%20user&context_id=${orgId}`,

      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const { data, refetch: refetchDepartments } = useQuery({
    queryKey: ["departments", orgId],
    queryFn: getDepartments,
  });

  const updateUserDetails = async (dep: DictType) => {
    const response = await axios.post(
      SERVER_URL + EDIT_USER_ORG_PARAMETER_ENDPOINT,

      {
        user_id: userId,
        id_department: dep.id,
        organization_id: orgId,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const updateUserDepartmentMutation = useMutation({
    mutationKey: [`department`],
    mutationFn: updateUserDetails,
    onSuccess: (data) => {
      setOpen(false);

      refetch && refetch();
    },
    onError: (error: DictType) => {
      const errorResponse = error?.response?.data.message;
      toast({
        title: translate("Error"),
        description: errorResponse ||
                translate(`Error occurred while updating`) +
                  " " +
                  translate(`department`),
        variant: "error",
      });
    },
  });

  useEffect(() => {
    department && setValue(department);
  }, [department]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className="w-full h-8 text-left flex items-center justify-start p-1 text-muted-foreground"
        >
          {department ? department : translate("Select Department")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0">
        <Command className={isHebrew ? "rtl" : "ltr"}>
          <CommandInput
            placeholder={translate("Search for department") + "..."}
          />
          <CommandList className={cn(isHebrew ? "rtl" : "ltr")}>
            <CommandEmpty>{translate("No departments found")}.</CommandEmpty>
            <CommandGroup>
              {data?.map((department: DictType) => (
                <CommandItem
                  key={department.id}
                  value={department?.name}
                  onSelect={(currentValue: any) => {
                    setValue(department?.name);
                    updateUserDepartmentMutation.mutate(department);
                  }}
                  className={cn(
                    "flex flex-col text-left items-start my-1 text-black",
                  )}
                >
                  {department.name}
                  <Separator className="mt-1" />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        <div className="p-2">
          {/* Add new department */}
          <AddDepartment refetch={refetchDepartments} />
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DepartmentsSelector;
